import React from "react";
import {
  useAnyPermissionWithLoading,
  useHasPermissionWithLoading,
} from "../../hooks/useHasPermission";
import ForbiddenPage from "../../pages/forbidden";
import { Box, CircularProgress } from "@mui/material";
import { useTenant } from "../../contexts/tenant-context";


export interface GuardedRouteProps {
  feature?: string;
  featureAny?: string[];
  requireGlobalAdmin?: boolean;
  element: React.ReactElement;
  fallback?: React.ReactElement;
}

export const GuardedRoute: React.FC<GuardedRouteProps> = ({
  feature,
  featureAny,
  requireGlobalAdmin,
  element,
  fallback,
}) => {
  const { isGlobalAdmin } = useTenant();

  const { hasPermission, isLoading } = useHasPermissionWithLoading(
    feature ?? "",
    "read"
  );
  const anyPermissionResult = useAnyPermissionWithLoading(featureAny ?? [], "read");
  const shouldUseAnyFeatureCheck = Boolean(featureAny?.length);
  const routeHasPermission = shouldUseAnyFeatureCheck
    ? anyPermissionResult.hasPermission
    : hasPermission;
  const routeIsLoading = shouldUseAnyFeatureCheck
    ? anyPermissionResult.isLoading
    : isLoading;

  // Global admin check
  if (requireGlobalAdmin && !isGlobalAdmin) {
    return fallback || <ForbiddenPage />;
  }

  // Show loading while permissions load
  if ((feature || shouldUseAnyFeatureCheck) && routeIsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Feature permission check
  if ((feature || shouldUseAnyFeatureCheck) && !routeHasPermission) {
    return fallback || <ForbiddenPage />;
  }

  return element;
};